import { useEffect, useState } from 'react';
import { useT } from '../i18n/locale-context';
import { apiRequest } from '../lib/api';
import { countryConfig } from '../country';

/** `GET /catalog/freshness` — see `services/api/src/modules/catalog/source-freshness.*`. */
export interface SourceFreshness {
  stale: boolean;
  since: string | null;
}

/** Spain only: the one deployment whose catalogue can go quiet because PLACSP did. */
export const SOURCE_FRESHNESS_ON = countryConfig.code === 'es';

/** One request per page load, shared by every catalogue mount (the API caches 10 min). */
let pending: Promise<SourceFreshness | null> | null = null;

export function fetchSourceFreshness(): Promise<SourceFreshness | null> {
  pending ??= apiRequest<SourceFreshness>('/catalog/freshness').catch(() => {
    pending = null; // a failed check is retried on the next mount, never shown
    return null;
  });
  return pending;
}

/** Test seam: forget the shared request. */
export function resetSourceFreshnessForTest(): void {
  pending = null;
}

/** «19 de octubre» — the day the last entry was published, Madrid. */
export function freshnessDate(iso: string): string {
  return new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'long',
    timeZone: 'Europe/Madrid',
  }).format(new Date(iso));
}

/**
 * MiLicita: a quiet one-line notice while PLACSP's main feed (643) publishes nothing new —
 * «La Plataforma … no publica licitaciones nuevas desde el 19 de octubre. Mostramos lo
 * último disponible.» The ingestion watchdog decides «stale» (`placsp-freshness.ts`).
 *
 * Fetched AFTER MOUNT only, so a prerendered page never bakes it in and hydration never
 * mismatches; renders nothing while fresh, on error, and on every UZ/KZ build.
 */
export function SourceFreshnessNotice() {
  const t = useT();
  const [since, setSince] = useState<string | null>(null);
  useEffect(() => {
    if (!SOURCE_FRESHNESS_ON) return;
    let alive = true;
    void fetchSourceFreshness().then((r) => {
      if (alive && r?.stale && r.since && !Number.isNaN(Date.parse(r.since))) setSince(r.since);
    });
    return () => {
      alive = false;
    };
  }, []);
  if (!since) return null;
  return (
    <p
      role="status"
      data-testid="source-freshness-notice"
      className="mb-3 rounded-md border border-edge bg-bone-surface px-3 py-2 text-[12px] leading-[1.45] text-ink-muted"
    >
      {t('catalog.sourceStale', { date: freshnessDate(since) })}
    </p>
  );
}
